import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SortAndFilterByRoutingService {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  public readonly filters = signal<{ [key: string]: string | string[] } | null>(null);
  public readonly sorting = signal<{ [key: string]: 'asc' | 'desc' } | null>(null);

  constructor() {
    // example url = http://localhost:4201/orders?filters=status%3DNew;production_method%3D%5BPRINTING,HYBRID%5D&sorting=date_created%3Ddesc
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      const filters: { [key: string]: string | string[] } = {};
      const sorting: { [key: string]: 'asc' | 'desc' } = {};

      if (params['filters']) {
        const filtersString = params['filters'] as string;
        const filtersArray = filtersString.split(';');
        filtersArray.forEach((filter) => {
          const [key, value] = filter.split('=');
          if (value.startsWith('[') && value.endsWith(']')) {
            // array value
            const arrayValue = value
              .substring(1, value.length - 1)
              .split(',')
              .map((v) => v.trim());
            filters[key] = arrayValue;
          } else {
            // string value
            filters[key] = value;
          }
        });
      }

      if (params['sorting']) {
        const sortingString = params['sorting'] as string;
        const sortingArray = sortingString.split(',');

        sortingArray.forEach((sort) => {
          const [key, value] = sort.split('=');
          sorting[key] = value as 'asc' | 'desc';
        });
      }

      this.filters.set(filters);
      this.sorting.set(sorting);
    });
  }

  public setRoutingBySorting(sorting: { [key: string]: 'asc' | 'desc' } | null) {
    let sortingAsString;
    if (sorting) {
      const sortingArray = Object.entries(sorting).map(([key, value]) => `${key}=${value}`);
      sortingAsString = sortingArray.join(',');
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        sorting: sortingAsString || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  public setRoutingByFilters(filters: { [key: string]: string | string[] } | null) {
    let filtersAsString;

    if (filters) {
      const filtersArray = Object.entries(filters)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([key, value]) => value !== undefined && value !== null)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return `${key}=[${value.join(',')}]`;
          } else {
            return `${key}=${value}`;
          }
        });
      filtersAsString = filtersArray.join(';');
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        filters: filtersAsString || null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
